import React, { useEffect, useState } from "react";
import bgVideo from "../../assets/finance-bg.mp4";
import AOS from "aos";
import "aos/dist/aos.css";
import PageLoader from "../../components/PageLoader/PageLoader";
import ProgressBar from "../../components/ProgressBar/ProgressBar";
import Navbar from "../../components/Navbar/Navbar";
import Hero from "../../components/Hero/Hero";
import Footer from "../../components/Footer/Footer";
import { MdBarChart, MdLock, MdTrendingUp, MdLanguage, MdChat, MdSavings } from "react-icons/md";
import { FaBolt, FaHandshake, FaCrown, FaHeart, FaUserTie, FaUsers, FaUser } from "react-icons/fa";
import { AiOutlineAim, AiOutlineRocket } from "react-icons/ai";
import { BsBank2, BsHourglassSplit, BsGraphUp } from "react-icons/bs";
import { GiMoneyStack } from "react-icons/gi";

export const RippleButton = ({ children, style = {}, className = '', onClick }) => {
    const handleClick = (e) => {
        const btn = e.currentTarget;
        const span = document.createElement('span');
        const sz = Math.max(btn.clientWidth, btn.clientHeight);
        const rect = btn.getBoundingClientRect();
        span.className = 'ripple-span';
        Object.assign(span.style, {
            width: sz + 'px', height: sz + 'px',
            left: e.clientX - rect.left - sz / 2 + 'px',
            top: e.clientY - rect.top - sz / 2 + 'px',
        });
        btn.querySelectorAll('.ripple-span').forEach(s => s.remove());
        btn.appendChild(span);
        onClick && onClick(e);
    };
    return (
        <button className={`btn ${className}`} style={style} onClick={handleClick}>
            {children}
        </button>
    );
};

/* ============================================================
   COUNTER HOOK
   ============================================================ */
const useCounter = (end, duration = 2200, delay = 400) => {
    const [value, setValue] = useState(0);
    useEffect(() => {
        const timer = setTimeout(() => {
            let start = null;
            const step = (ts) => {
                if (!start) start = ts;
                const p = Math.min((ts - start) / duration, 1);
                const ease = 1 - Math.pow(1 - p, 4);
                setValue(Math.floor(ease * end));
                if (p < 1) requestAnimationFrame(step);
            };
            requestAnimationFrame(step);
        }, delay);
        return () => clearTimeout(timer);
    }, [end, duration, delay]);
    return value;
};

export const StatCard = ({ label, value, max, sub, accentColor, iconChar: IconComponent, delay }) => {
    const pct = Math.min((value / max) * 100, 100);
    return (
        <div style={{
            position: 'relative', overflow: 'hidden',
            borderRadius: 20,
            background: 'rgba(255,255,255,0.06)',
            border: '1px solid rgba(255,255,255,0.12)',
            backdropFilter: 'blur(20px)',
            padding: '1.4rem 1.5rem',
            cursor: 'default',
            animation: `cardIn 0.7s cubic-bezier(0.34,1.3,0.64,1) ${delay}s both`,
            transition: 'transform 0.3s cubic-bezier(0.34,1.56,0.64,1), border-color 0.3s',
        }}
            onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-6px) scale(1.02)'; e.currentTarget.style.borderColor = accentColor + '60'; }}
            onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0) scale(1)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.12)'; }}
        >
            {/* glow blob */}
            <div style={{
                position: 'absolute', top: -32, right: -32, width: 100, height: 100,
                borderRadius: '50%', background: accentColor, opacity: 0.18, filter: 'blur(28px)',
                pointerEvents: 'none',
            }} />
            {/* icon */}
            <div style={{
                position: 'absolute', top: 14, right: 16,
                fontSize: 28, opacity: 0.15, lineHeight: 1,
            }}>{IconComponent && <IconComponent size={28} color={accentColor} />}</div>

            <div style={{ position: 'relative', zIndex: 1 }}>
                <p style={{ fontSize: 10, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.45)', marginBottom: '0.65rem', fontFamily: 'Syne, sans-serif' }}>
                    {label}
                </p>
                <h2 style={{
                    fontSize: 26, fontWeight: 700, color: '#fff',
                    fontFamily: 'Syne, sans-serif', marginBottom: '1rem',
                    animation: `countUp 0.5s ease ${delay + 0.2}s both`,
                }}>
                    FC {value.toLocaleString()}
                </h2>
                <ProgressBar percent={pct} color={accentColor} delay={(delay + 0.5) * 1000} />
                <p style={{ fontSize: 11, marginTop: '0.6rem', color: 'rgba(255,255,255,0.35)' }}>{sub}</p>
            </div>
        </div>
    );
};

const SectionHeading = ({ badge, title, subtitle, light = false }) => (
    <div style={{ textAlign: 'center', marginBottom: '3rem' }} data-aos="fade-up">
        <span style={{
            display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
            padding: '0.4rem 1rem', borderRadius: 99,
            background: light ? 'rgba(249,115,22,0.15)' : 'rgba(249,115,22,0.08)',
            border: `1px solid ${light ? 'rgba(249,115,22,0.3)' : 'rgba(249,115,22,0.15)'}`,
            fontSize: 12, fontWeight: 600,
            color: light ? '#fdba74' : '#f97316',
            fontFamily: 'Syne, sans-serif', letterSpacing: '0.06em',
            marginBottom: '1rem',
        }}>{badge}</span>
        <h2 className="syne" style={{
            fontSize: 'clamp(1.6rem, 3vw, 2.6rem)', fontWeight: 800,
            color: light ? '#fff' : '#1a1208', lineHeight: 1.15, marginBottom: '1rem',
            letterSpacing: '-0.02em',
        }}>{title}</h2>
        {subtitle && (
            <p style={{ color: light ? 'rgba(255,255,255,0.5)' : '#6b5e4e', maxWidth: 560, margin: '0 auto', lineHeight: 1.7, fontSize: 15 }}>
                {subtitle}
            </p>
        )}
    </div>
);

/* ============================================================
   CARD COMPONENT
   ============================================================ */
const InfoCard = ({ icon: IconComponent, iconBg, title, desc, dark = false, style: extraStyle = {} }) => (
    <div className="hover-lift" style={{
        borderRadius: 20, overflow: 'hidden', position: 'relative',
        background: dark ? 'rgba(255,255,255,0.04)' : '#fff',
        border: dark ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(249,115,22,0.1)',
        padding: '1.5rem',
        ...extraStyle,
    }} data-aos="fade-up">
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: iconBg, borderRadius: '20px 20px 0 0' }} />
        <div style={{
            width: 52, height: 52, borderRadius: 14,
            background: iconBg + '20', border: `1px solid ${iconBg}30`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 24, marginBottom: '1rem',
        }}>{IconComponent && <IconComponent size={24} color={iconBg} />}</div>
        <h3 className="syne" style={{ fontSize: 17, fontWeight: 700, color: dark ? '#fff' : '#1a1208', marginBottom: '0.5rem' }}>{title}</h3>
        <p style={{ fontSize: 14, color: dark ? 'rgba(255,255,255,0.45)' : '#6b5e4e', lineHeight: 1.7 }}>{desc}</p>
    </div>
);

/* ============================================================
   MAIN HOME COMPONENT
   ============================================================ */
const Home = () => {
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('objectifs');
    const [openFaq, setOpenFaq] = useState(null);

    const wallet = useCounter(25000, 2000, 600);
    const loanLimit = useCounter(100000, 2200, 700);
    const activeLoan = useCounter(40000, 1800, 800);

    useEffect(() => {
        AOS.init({ duration: 800, once: true, offset: 60 });
        const t = setTimeout(() => setLoading(false), 1400);
        return () => clearTimeout(t);
    }, []);

    return (
        <div style={{ fontFamily: 'DM Sans, sans-serif', background: '#f8f5f0', color: '#1a1208', overflowX: 'hidden' }}>
            <PageLoader loading={loading} />
            <Navbar />

            {/* ── HERO ── */}
            <Hero bgVideo={bgVideo} wallet={wallet} loanLimit={loanLimit} activeLoan={activeLoan} />

            {/* ── OBJECTIFS & MISSION ── */}
            <section style={{ maxWidth: 1140, margin: '0 auto', padding: '4rem 1.25rem' }} data-aos="fade-up">
                <div style={{
                    background: '#fff', borderRadius: 24,
                    border: '1px solid rgba(249,115,22,0.1)',
                    overflow: 'hidden',
                    boxShadow: '0 4px 40px rgba(249,115,22,0.06)',
                }}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr' }}>
                        {/* Tab sidebar */}
                        <div style={{ background: '#faf7f4', borderRight: '1px solid rgba(249,115,22,0.08)', padding: '1.25rem', display: 'flex', flexDirection: 'column', gap: '0.5rem', minWidth: 160 }}>
                            {['objectifs', 'mission'].map(tab => (
                                <button key={tab} onClick={() => setActiveTab(tab)} style={{
                                    padding: '0.7rem 1rem', borderRadius: 10, border: 'none', cursor: 'pointer',
                                    fontFamily: 'Syne, sans-serif', fontWeight: 600, fontSize: 13,
                                    textAlign: 'left', transition: 'all 0.2s',
                                    background: activeTab === tab ? '#f97316' : 'transparent',
                                    color: activeTab === tab ? '#fff' : '#6b5e4e',
                                }}>
                                    {tab.charAt(0).toUpperCase() + tab.slice(1)}
                                </button>
                            ))}
                        </div>
                        {/* Content */}
                        <div style={{ padding: '2rem' }}>
                            {activeTab === 'objectifs' ? (
                                <>
                                    <h3 className="syne" style={{ fontSize: 18, fontWeight: 700, color: '#f97316', marginBottom: '1rem' }}>Les buts et objectifs</h3>
                                    <ul style={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
                                        {[
                                            'Soutenir les activités génératrices de revenu de ses membres',
                                            'Accompagner ses membres en leur accordant des micro-crédits',
                                            'Former les membres en gestion financière et alphabétisation',
                                            'Conseils financiers pour une meilleure gestion de l\'argent',
                                            'Encourager les investissements pour lutter contre la pauvreté',
                                            'Fournir des services financiers inclusifs',
                                        ].map((item, i) => (
                                            <li key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '0.6rem', fontSize: 14, color: '#3d3020', lineHeight: 1.65 }}>
                                                <span style={{ width: 18, height: 18, borderRadius: '50%', background: 'rgba(249,115,22,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 2, fontSize: 9, color: '#f97316', fontWeight: 700 }}>✓</span>
                                                {item}
                                            </li>
                                        ))}
                                    </ul>
                                </>
                            ) : (
                                <>
                                    <h3 className="syne" style={{ fontSize: 18, fontWeight: 700, color: '#f97316', marginBottom: '1rem' }}>Notre mission</h3>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                                        {[
                                            "Être l'une des associations de référence dans le pays répondant aux besoins financiers et sociaux de ses membres.",
                                            "Rendre les entrepreneurs et associés prospères en leur donnant les moyens nécessaires pour l'acquisition des actifs.",
                                            "Combattre la pauvreté par l'inclusion financière grâce à des produits et services adaptés.",
                                        ].map((p, i) => (
                                            <p key={i} style={{ fontSize: 14, color: '#3d3020', lineHeight: 1.75 }}>{p}</p>
                                        ))}
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </section>

            {/* ── ABOUT US ── */}
            <section style={{ position: 'relative', padding: '5rem 1.25rem', background: '#fff', overflow: 'hidden' }} data-aos="fade-up">
                <div style={{ position: 'absolute', top: 0, left: 0, width: 400, height: 400, borderRadius: '50%', background: 'radial-gradient(circle, rgba(249,115,22,0.07), transparent)', pointerEvents: 'none' }} />
                <div style={{ position: 'absolute', bottom: 0, right: 0, width: 400, height: 400, borderRadius: '50%', background: 'radial-gradient(circle, rgba(96,165,250,0.06), transparent)', pointerEvents: 'none' }} />
                <div style={{ position: 'relative', zIndex: 1, maxWidth: 1140, margin: '0 auto' }}>
                    <SectionHeading badge="À PROPOS" title="Découvrez TUMAINI LETU" subtitle="Organisation engagée dans l'accompagnement financier, l'épargne communautaire et le développement économique durable." />
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 20 }}>
                        <InfoCard icon={AiOutlineAim} iconBg="#f97316" title="Notre vision" desc="Devenir une institution de référence dans l'inclusion financière et le soutien au développement socio-économique des communautés." />
                        <InfoCard icon={AiOutlineRocket} iconBg="#3b82f6" title="Notre mission" desc="Offrir des solutions financières accessibles, innovantes et adaptées afin d'améliorer les conditions de vie de nos membres." />
                        <InfoCard icon={FaHandshake} iconBg="#10b981" title="Nos valeurs" desc={<div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', marginTop: '0.5rem' }}>{['Solidarité', 'Transparence', 'Innovation', 'Inclusion', 'Responsabilité'].map(v => <span key={v} style={{ padding: '0.25rem 0.7rem', borderRadius: 99, background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.2)', fontSize: 12, color: '#065f46', fontWeight: 500 }}>{v}</span>)}</div>} />
                    </div>

                    {/* Stats bar */}
                    <div style={{
                        marginTop: '2rem', borderRadius: 20, overflow: 'hidden',
                        background: 'linear-gradient(135deg, #f97316 0%, #ea6c0c 100%)',
                        padding: '2rem 2.5rem',
                        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '1.5rem',
                    }} data-aos="fade-up">
                        {[['5K+', 'Membres actifs'], ['10+', "Années d'expérience"], ['120M+', 'FC en crédits'], ['98%', 'Taux de satisfaction']].map(([n, l]) => (
                            <div key={l} style={{ textAlign: 'center' }}>
                                <p className="syne" style={{ fontSize: 32, fontWeight: 800, color: '#fff', marginBottom: '0.3rem' }}>{n}</p>
                                <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.7)' }}>{l}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ── LOAN OVERVIEW ── */}
            <section style={{ maxWidth: 1140, margin: '0 auto', padding: '3rem 1.25rem' }} data-aos="fade-up">
                <h2 className="syne" style={{ fontSize: 22, fontWeight: 700, marginBottom: '1.25rem', color: '#1a1208' }}>Aperçu du prêt</h2>
                <div style={{
                    background: '#fff', borderRadius: 20, padding: '1.75rem',
                    boxShadow: '0 4px 30px rgba(249,115,22,0.08)',
                }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '0.75rem' }}>
                        <p style={{ fontWeight: 600, fontSize: 15 }}>Prêt actif</p>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '1.25rem' }}>
                        <p style={{ fontSize: 14, color: '#6b5e4e' }}>Prochaine date de paiement</p>
                        <p style={{ fontSize: 14 }}>25 Mai 2026</p>
                    </div>
                    <div style={{ background: '#f8f5f0', borderRadius: 99, height: 8, overflow: 'hidden' }}>
                        <div style={{ height: '100%', width: '60%', borderRadius: 99, background: 'linear-gradient(90deg, #f97316, #fb923c)', position: 'relative', overflow: 'hidden' }}>
                            <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)', animation: 'shine 2.5s ease-in-out infinite' }} />
                        </div>
                    </div>
                    <p style={{ marginTop: '0.6rem', fontSize: 12, color: '#6b5e4e' }}>60% payés — continuez pour finaliser votre prêt</p>
                </div>
            </section>

            {/* ── MEMBERS QUALITY ── */}
            <section style={{ position: 'relative', padding: '5rem 1.25rem', background: '#0d0a06', overflow: 'hidden' }} data-aos="fade-up">
                <div style={{ position: 'absolute', top: 0, left: 0, width: 360, height: 360, borderRadius: '50%', background: 'rgba(249,115,22,0.08)', filter: 'blur(80px)', pointerEvents: 'none' }} />
                <div style={{ position: 'absolute', bottom: 0, right: 0, width: 360, height: 360, borderRadius: '50%', background: 'rgba(96,165,250,0.06)', filter: 'blur(80px)', pointerEvents: 'none' }} />
                <div style={{ position: 'relative', zIndex: 1, maxWidth: 1140, margin: '0 auto' }}>
                    <SectionHeading light badge="MEMBRES" title="Qualité des membres" subtitle="TUMAINI LETU ASBL comprend 3 types de membres" />
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 20 }}>
                        <InfoCard dark icon={FaCrown} iconBg="#f97316" title="Membres fondateurs" desc="Ont participé à la création de l'association. Ils constituent le Conseil d'Administration." />
                        <InfoCard dark icon={FaHeart} iconBg="#3b82f6" title="Membres donateurs" desc="Personnes morales ou physiques apportant un soutien matériel, intellectuel ou financier à TUMAINI LETU." />
                        <InfoCard dark icon={FaHandshake} iconBg="#10b981" title="Membres adhérents" desc="Toute personne physique ou morale acceptant librement d'adhérer aux objectifs de TUMAINI LETU et de participer à ses activités." />
                    </div>
                </div>
            </section>

            {/* ── PRODUCTS ── */}
            <section style={{ position: 'relative', padding: '5rem 1.25rem', background: '#0d0a06', overflow: 'hidden' }} data-aos="fade-up">
                <div style={{ position: 'absolute', bottom: 0, left: 0, width: 400, height: 400, borderRadius: '50%', background: 'rgba(249,115,22,0.06)', filter: 'blur(100px)', pointerEvents: 'none' }} />
                <div style={{ position: 'relative', zIndex: 1, maxWidth: 1140, margin: '0 auto' }}>
                    <SectionHeading light badge="PRODUITS" title="Les produits de TUMAINI LETU" subtitle="Solutions financières adaptées pour promouvoir l'épargne, l'investissement et l'accès au crédit." />
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 20 }}>
                        {/* Epargne */}
                        <div className="hover-lift" style={{ borderRadius: 20, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', padding: '1.75rem', position: 'relative', overflow: 'hidden' }} data-aos="fade-up">
                            <div style={{ fontSize: 28, marginBottom: '1rem' }}><GiMoneyStack size={28} color="#f97316" /></div>
                            <h3 className="syne" style={{ fontSize: 18, fontWeight: 700, color: '#fff', marginBottom: '0.5rem' }}>Produits d'épargne</h3>
                            <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.45)', marginBottom: '1.5rem', lineHeight: 1.7 }}>Solutions sécurisées pour mieux gérer et faire croître vos ressources.</p>
                            {[[BsBank2, '#f97316', 'Dépôt à vue', 'Gestion quotidienne des fonds'], [BsHourglassSplit, '#3b82f6', 'Dépôt à terme (DAT)', 'Épargne sur 3, 6 ou 12 mois'], [FaHandshake, '#10b981', 'Épargne solidarité', 'Soutien communautaire'], [MdSavings, '#8b5cf6', 'Épargne études', 'Frais scolaires'], [BsGraphUp, '#ec4899', 'Épargne investissement', 'Projets personnels']].map(([IconComp, color, t, s]) => (
                                <div key={t} style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', padding: '0.65rem 0', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                                    <div style={{ width: 36, height: 36, borderRadius: 10, background: color + '20', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><IconComp size={18} color={color} /></div>
                                    <div>
                                        <p style={{ fontSize: 13, fontWeight: 600, color: '#fff' }}>{t}</p>
                                        <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)' }}>{s}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                        {/* Credit */}
                        <div className="hover-lift" style={{ borderRadius: 20, background: 'linear-gradient(145deg, #ea6c0c, #f97316)', padding: '1.75rem', position: 'relative', overflow: 'hidden' }} data-aos="fade-up">
                            <div style={{ position: 'absolute', bottom: -40, left: -40, width: 200, height: 200, borderRadius: '50%', background: 'rgba(0,0,0,0.15)', filter: 'blur(40px)', pointerEvents: 'none' }} />
                            <div style={{ position: 'relative', zIndex: 1 }}>
                                <div style={{ fontSize: 28, marginBottom: '1rem' }}><GiMoneyStack size={28} color="#fff" /></div>
                                <h3 className="syne" style={{ fontSize: 18, fontWeight: 700, color: '#fff', marginBottom: '0.5rem' }}>Produits de crédit</h3>
                                <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.7)', marginBottom: '1.5rem', lineHeight: 1.7 }}>Financements accessibles pour soutenir vos activités économiques.</p>
                                {[[FaUsers, 'Crédit groupe solidaire', 'Financement collectif basé sur la solidarité'], [FaUser, 'Crédit individuel', 'Appui financier personnalisé pour les membres']].map(([IconComp, t, s]) => (
                                    <div key={t} style={{ borderRadius: 14, background: 'rgba(0,0,0,0.15)', border: '1px solid rgba(255,255,255,0.15)', padding: '1rem 1.25rem', marginBottom: '0.75rem', display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                                        <div style={{ width: 44, height: 44, borderRadius: 12, background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><IconComp size={20} color="#f97316" /></div>
                                        <div>
                                            <p style={{ fontSize: 14, fontWeight: 600, color: '#fff' }}>{t}</p>
                                            <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.6)', marginTop: 2 }}>{s}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ── WHY US ── */}
            <section style={{ position: 'relative', padding: '5rem 1.25rem', background: '#fff', overflow: 'hidden' }} data-aos="fade-up">
                <div style={{ position: 'absolute', top: 0, left: 0, width: 360, height: 360, borderRadius: '50%', background: 'radial-gradient(circle, rgba(249,115,22,0.06), transparent)', pointerEvents: 'none' }} />
                <div style={{ position: 'relative', zIndex: 1, maxWidth: 1140, margin: '0 auto' }}>
                    <SectionHeading badge="POURQUOI NOUS" title="Pourquoi choisir TUMAINI LETU ?" subtitle="Des solutions fiables, accessibles et adaptées aux réalités locales pour votre autonomie économique." />
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 18 }}>
                        {[
                            { icon: FaBolt, iconBg: '#f97316', title: 'Procédure rapide et simplifiée', desc: 'Démarches simples et rapides pour accéder à nos services.' },
                            { icon: MdLock, iconBg: '#3b82f6', title: 'Sécurité des fonds', desc: 'Économies, transactions protégées par une gestion responsable et transparente.' },
                            { icon: FaHandshake, iconBg: '#10b981', title: 'Accompagnement', desc: 'Chaque membre bénéficie d\'un suivi adapté à ses besoins.' },
                            { icon: MdTrendingUp, iconBg: '#8b5cf6', title: 'Développement', desc: 'Soutien aux projets et activités génératrices de revenus.' },
                            { icon: MdLanguage, iconBg: '#ec4899', title: 'Inclusion financière', desc: 'Services accessibles à tous pour renforcer l\'autonomie.' },
                            { icon: MdChat, iconBg: '#06b6d4', title: 'Support réactif', desc: 'Équipe disponible pour répondre rapidement à vos questions.' },
                        ].map(c => <InfoCard key={c.title} {...c} />)}
                    </div>
                    {/* CTA banner */}
                    <div style={{
                        marginTop: '2.5rem', borderRadius: 20, overflow: 'hidden',
                        background: 'linear-gradient(135deg, #f97316, #ea6c0c)',
                        padding: '2.5rem', textAlign: 'center', position: 'relative',
                    }} data-aos="fade-up">
                        <div style={{ position: 'absolute', top: -40, right: -40, width: 200, height: 200, borderRadius: '50%', background: 'rgba(255,255,255,0.07)', filter: 'blur(30px)', pointerEvents: 'none' }} />
                        <h3 className="syne" style={{ fontSize: 'clamp(1.3rem, 2.5vw, 2rem)', fontWeight: 800, color: '#fff', marginBottom: '0.75rem' }}>
                            Construire un avenir financier plus stable pour tous
                        </h3>
                        <p style={{ color: 'rgba(255,255,255,0.65)', fontSize: 14, maxWidth: 500, margin: '0 auto 1.5rem', lineHeight: 1.7 }}>
                            TUMAINI LETU met l'humain au centre pour une croissance inclusive et durable.
                        </p>
                        <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '0.75rem' }}>
                            <RippleButton
                                style={{
                                    padding: '0.7rem 1.5rem',
                                    borderRadius: 12,
                                    background: '#fff',
                                    color: '#f97316',
                                    fontSize: 14,
                                    fontWeight: 600
                                }}
                                onClick={() => {
                                    window.location.href = "mailto:info@tumainiletu.com?subject=Demande%20d'adhésion";
                                }}
                            >
                                Nous rejoindre
                            </RippleButton>                            
                            <RippleButton style={{ padding: '0.7rem 1.5rem', borderRadius: 12, background: 'rgba(0,0,0,0.15)', color: '#fff', border: '1px solid rgba(255,255,255,0.25)', fontSize: 14, fontWeight: 600 }}>En savoir plus</RippleButton>
                        </div>
                    </div>
                </div>
            </section>

            {/* ── TEAM ── */}
            <section style={{ position: 'relative', padding: '5rem 1.25rem', background: '#f8f5f0', overflow: 'hidden' }} data-aos="fade-up">
                <div style={{ position: 'relative', zIndex: 1, maxWidth: 1140, margin: '0 auto' }}>
                    <SectionHeading badge="ÉQUIPE" title="Les leaders" subtitle="Une équipe engagée travaillant chaque jour pour accompagner les membres dans leur développement." />
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 20 }}>
                        {[
                            // { name: 'Nom du président', role: 'Président', icon: FaUserTie, color: '#f97316' },
                            { name: 'KWABO BADERHA ALAIN', role: 'Gérant', icon: FaUserTie, color: '#3b82f6' },
                            { name: 'RUKIRA NSHIMIYE MARTIALE', role: 'Gérant Adjoint', icon: GiMoneyStack, color: '#10b981' },
                            { name: 'BULANGALIRE POLEPOLE JEAN-ESPOR', role: 'DRH', icon: MdBarChart, color: '#8b5cf6' },
                        ].map(m => (
                            <div key={m.role} className="hover-lift" style={{
                                background: '#fff', borderRadius: 20, overflow: 'hidden',
                                border: '1px solid rgba(249,115,22,0.08)', textAlign: 'center',
                            }} data-aos="fade-up">
                                <div style={{ height: 4, background: m.color }} />
                                <div style={{ padding: '1.5rem 1.25rem' }}>
                                    <div style={{
                                        width: 72, height: 72, borderRadius: '50%', margin: '0 auto 1rem',
                                        background: m.color + '18', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        border: `2px solid ${m.color}30`,
                                    }}><m.icon size={32} color={m.color} /></div>
                                    <h3 className="syne" style={{ fontSize: 15, fontWeight: 700, color: '#1a1208', marginBottom: '0.25rem' }}>{m.name}</h3>
                                    <p style={{ fontSize: 12, color: m.color, fontWeight: 600, letterSpacing: '0.06em', marginBottom: '1rem', fontFamily: 'Syne, sans-serif' }}>{m.role}</p>
                                    <div style={{ display: 'flex', justifyContent: 'center', gap: '0.5rem' }}>
                                        {[[MdLanguage, 'web'], [FaUserTie, 'cv'], [MdChat, 'mail']].map(([IconComp, key]) => (
                                            <button key={key} style={{ width: 34, height: 34, borderRadius: 10, background: '#f8f5f0', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s' }}
                                                onMouseEnter={e => { e.currentTarget.style.background = m.color; }}
                                                onMouseLeave={e => { e.currentTarget.style.background = '#f8f5f0'; }}
                                            ><IconComp size={14} color={m.color} /></button>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ── TESTIMONIALS ── */}
            <section style={{ position: 'relative', padding: '5rem 1.25rem', background: '#fff', overflow: 'hidden' }} data-aos="fade-up">
                <div style={{ position: 'relative', zIndex: 1, maxWidth: 1140, margin: '0 auto' }}>
                    <SectionHeading badge="TÉMOIGNAGES" title="Ce que disent nos membres" subtitle="Découvrez les expériences de membres ayant bénéficié de nos services financiers." />
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 20 }}>
                        {[
                            { name: 'Florence BAHATI', role: 'Entrepreneure', text: "Grâce au crédit individuel, j'ai pu développer mon commerce et augmenter mes revenus.", avatar: FaUser },
                            { name: 'Vyoteni Mulinda OLIVIER', role: 'Membre adhérent', text: "L'épargne solidarité m'a beaucoup aidé à mieux gérer mes finances familiales.", avatar: FaUser },
                            // { name: 'Esther KAHINDO', role: 'Commerçante', text: "L'accompagnement et les conseils reçus m'ont permis de lancer mon projet avec confiance.", avatar: FaUser },
                        ].map(t => (
                            <div key={t.name} className="hover-lift" style={{
                                borderRadius: 20, background: '#faf7f4',
                                border: '1px solid rgba(249,115,22,0.08)',
                                padding: '1.5rem', position: 'relative', overflow: 'hidden',
                            }} data-aos="fade-up">
                                <div style={{ position: 'absolute', top: -24, right: -24, width: 80, height: 80, borderRadius: '50%', background: 'rgba(249,115,22,0.06)', pointerEvents: 'none' }} />
                                <div style={{ fontSize: 24, color: '#f97316', marginBottom: '0.75rem', opacity: 0.4 }}>"</div>
                                <p style={{ fontSize: 14, color: '#3d3020', lineHeight: 1.75, marginBottom: '1.25rem', fontStyle: 'italic' }}>{t.text}</p>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                                    <div style={{ width: 42, height: 42, borderRadius: '50%', background: 'linear-gradient(135deg, #f97316, #fb923c)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><t.avatar size={20} color="#fff" /></div>
                                    <div>
                                        <p style={{ fontSize: 13, fontWeight: 700, color: '#1a1208', fontFamily: 'Syne, sans-serif' }}>{t.name}</p>
                                        <p style={{ fontSize: 11, color: '#f97316' }}>{t.role}</p>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ── CONTACT ── */}
            <section style={{ maxWidth: 800, margin: '0 auto', padding: '4rem 1.25rem' }} data-aos="fade-up">
                <SectionHeading badge="CONTACT" title="Contactez-nous" />
                <div style={{ background: '#fff', borderRadius: 24, padding: '2rem', border: '1px solid rgba(249,115,22,0.1)' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0.75rem' }}>
                        {[
                            { ph: 'Nom complet', type: 'text' },
                            { ph: 'Adresse email', type: 'email' },
                        ].map(f => (
                            <input key={f.ph} type={f.type} placeholder={f.ph} style={{
                                padding: '0.85rem 1rem', borderRadius: 12,
                                border: '1px solid rgba(249,115,22,0.15)',
                                background: '#faf7f4', fontSize: 14, color: '#1a1208',
                                outline: 'none', fontFamily: 'DM Sans, sans-serif',
                                transition: 'border-color 0.2s',
                            }}
                                onFocus={e => e.target.style.borderColor = '#f97316'}
                                onBlur={e => e.target.style.borderColor = 'rgba(249,115,22,0.15)'}
                            />
                        ))}
                        <input type="tel" placeholder="Téléphone" style={{
                            padding: '0.85rem 1rem', borderRadius: 12,
                            border: '1px solid rgba(249,115,22,0.15)', gridColumn: '1/-1',
                            background: '#faf7f4', fontSize: 14, color: '#1a1208',
                            outline: 'none', fontFamily: 'DM Sans, sans-serif',
                            transition: 'border-color 0.2s',
                        }}
                            onFocus={e => e.target.style.borderColor = '#f97316'}
                            onBlur={e => e.target.style.borderColor = 'rgba(249,115,22,0.15)'}
                        />
                        <textarea rows={5} placeholder="Votre message..." style={{
                            padding: '0.85rem 1rem', borderRadius: 12, gridColumn: '1/-1',
                            border: '1px solid rgba(249,115,22,0.15)',
                            background: '#faf7f4', fontSize: 14, color: '#1a1208', resize: 'none',
                            outline: 'none', fontFamily: 'DM Sans, sans-serif',
                            transition: 'border-color 0.2s',
                        }}
                            onFocus={e => e.target.style.borderColor = '#f97316'}
                            onBlur={e => e.target.style.borderColor = 'rgba(249,115,22,0.15)'}
                        />
                    </div>
                    <div style={{ textAlign: 'center', marginTop: '1.25rem' }}>
                        <RippleButton style={{ padding: '0.8rem 2rem', borderRadius: 12, background: '#f97316', color: '#fff', fontSize: 14, fontWeight: 600 }}>
                            Envoyer le message
                        </RippleButton>
                    </div>
                </div>
            </section>

            {/* ── FAQ ── */}
            <section style={{ position: 'relative', padding: '5rem 1.25rem', background: '#f8f5f0', overflow: 'hidden' }} data-aos="fade-up">
                <div style={{ position: 'relative', zIndex: 1, maxWidth: 760, margin: '0 auto' }}>
                    <SectionHeading badge="FAQ" title="Questions fréquentes" subtitle="Trouvez rapidement les réponses aux questions les plus courantes sur TUMAINI LETU." />
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
                        {[
                            { q: "Comment puis-je obtenir un prêt ?", a: "Vous devez être membre actif, avoir un compte d'épargne et soumettre une demande auprès de notre équipe." },
                            { q: "Quels documents sont nécessaires ?", a: "Une pièce d'identité valide, une preuve de résidence et parfois des garanties selon le type de prêt." },
                            { q: "Combien de temps prend l'approbation ?", a: "En général 24h à 7 jours selon le montant et le type de crédit demandé." },
                            { q: "Puis-je retirer mon épargne à tout moment ?", a: "Selon le type de compte — certains retraits sont disponibles immédiatement, d'autres sont à terme." },
                            { q: "Comment devenir membre ?", a: "Remplissez un formulaire d'adhésion et payez les frais d'inscription définis par l'organisation." },
                        ].map((item, i) => (
                            <div key={i} onClick={() => setOpenFaq(openFaq === i ? null : i)} style={{
                                background: '#fff', borderRadius: 14, overflow: 'hidden',
                                border: `1px solid ${openFaq === i ? 'rgba(249,115,22,0.25)' : 'rgba(249,115,22,0.08)'}`,
                                cursor: 'pointer', transition: 'border-color 0.25s',
                            }} data-aos="fade-up">
                                <div style={{ padding: '1.1rem 1.25rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem' }}>
                                    <p style={{ fontSize: 14, fontWeight: 600, color: '#1a1208', fontFamily: 'Syne, sans-serif' }}>{item.q}</p>
                                    <div style={{
                                        width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
                                        background: openFaq === i ? '#f97316' : 'rgba(249,115,22,0.1)',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        fontSize: 16, color: openFaq === i ? '#fff' : '#f97316',
                                        transition: 'all 0.3s',
                                        transform: openFaq === i ? 'rotate(45deg)' : 'none',
                                        fontWeight: 300,
                                    }}>+</div>
                                </div>
                                <div style={{
                                    maxHeight: openFaq === i ? 200 : 0,
                                    overflow: 'hidden', transition: 'max-height 0.4s cubic-bezier(0.4,0,0.2,1)',
                                }}>
                                    <p style={{ padding: '0 1.25rem 1.1rem', fontSize: 13, color: '#6b5e4e', lineHeight: 1.75 }}>{item.a}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ── SUPPORT ── */}
            <section style={{ background: '#f8f5f0', padding: '3rem 1.25rem', textAlign: 'center' }} data-aos="fade-up">
                <div style={{ maxWidth: 500, margin: '0 auto' }}>
                    <h2 className="syne" style={{ fontSize: 22, fontWeight: 700, marginBottom: '0.6rem' }}>Besoin d'aide ?</h2>
                    <p style={{ fontSize: 14, color: '#6b5e4e', marginBottom: '1.25rem', lineHeight: 1.7 }}>Contactez le support pour toute assistance concernant votre compte.</p>
                    <RippleButton
                        style={{ padding: '0.75rem 1.75rem', borderRadius: 12, background: '#f97316', color: '#fff', fontSize: 14, fontWeight: 600 }}
                        onClick={() => window.location.href = "mailto:info@tumainiletu.com?subject=Demande%20d'assistance"}
                    >Contacter l'assistance</RippleButton>
                </div>
            </section>
            <Footer />
        </div>
    );
};

export default Home;
